import React, { useState, useEffect, useCallback } from "react"
import { useSearchParams } from "react-router-dom"
import UserApi from "../../services/users";
import NetworkApi from "../../services/networks";
import UsersTable from "../../tables/UsersTable"
import DashboardHeader from '../../components/DashboardHeader';
import { PopupboxContainer } from 'react-popupbox';
import LetterFilter from "../../components/LetterFilter";
import Pagination from "../../components/Pagination"
import useAuthErrorHandler from '../../hooks/useAuthErrorHandler';

function UsersPage(props) {
    const [searchParams, setSearchParams] = useSearchParams();
    const [allUsers, setAllUsers] = useState([]); // Store all users
    const [users, setUsers] = useState([]); // Display users for current page
    const [search, setSearch] = useState(searchParams.get('search') || '');
    const [searchStatus, setSearchStatus] = useState('All');
    const [selectedNetwork, setSelectedNetwork] = useState(searchParams.get('network') || '');
    const [networks, setNetworks] = useState([]);
    const [totalPages, setTotalPages] = useState(0);
    const [pageItemActive, setPageItemActive] = useState(1);
    const [activeLetter, setActiveLetter] = useState(searchParams.get('letter') || 'all');
    const [tenantId] = useState('1'); // Default tenant ID - this should come from context or props
    const itemsPerPage = 20;
    const handleAuthError = useAuthErrorHandler();

    const fetch = useCallback(async() => {
        const userApi = new UserApi(props.token);
        try {
            if (selectedNetwork && selectedNetwork !== '') {
                // Get users for specific network (tenant)
                let response = await userApi.getMembers(selectedNetwork);
                let networkUsers = response.members;
                
                // Apply search filter within network if search is active
                if (search && search.trim()) {
                    const trimmedSearch = search.trim().toLowerCase();
                    networkUsers = networkUsers.filter(user => 
                        user.first_name?.toLowerCase().includes(trimmedSearch) ||
                        user.last_name?.toLowerCase().includes(trimmedSearch) ||
                        user.email?.toLowerCase().includes(trimmedSearch)
                    );
                }
                // Apply letter filter within network if letter is active
                else if (activeLetter && activeLetter !== 'all') {
                    networkUsers = networkUsers.filter(user => 
                        user.first_name?.toLowerCase().startsWith(activeLetter.toLowerCase()) ||
                        user.last_name?.toLowerCase().startsWith(activeLetter.toLowerCase()) ||
                        user.email?.toLowerCase().startsWith(activeLetter.toLowerCase())
                    );
                }
                
                setAllUsers(networkUsers);
                setTotalPages(Math.ceil(networkUsers.length / itemsPerPage));
            }
            else if (activeLetter && activeLetter !== 'all') {
                // For all users, filter by letter
                let response = await userApi.all();
                let filteredUsers = response.users.filter(user => 
                    user.first_name?.toLowerCase().startsWith(activeLetter.toLowerCase()) ||
                    user.last_name?.toLowerCase().startsWith(activeLetter.toLowerCase()) ||
                    user.email?.toLowerCase().startsWith(activeLetter.toLowerCase())
                );
                setAllUsers(filteredUsers);
                setTotalPages(Math.ceil(filteredUsers.length / itemsPerPage));
            }   
            else if (search && search.trim()) {
                // Search all users
                let response = await userApi.all();
                const trimmedSearch = search.trim().toLowerCase();
                let filteredUsers = response.users.filter(user => 
                    user.first_name?.toLowerCase().includes(trimmedSearch) ||
                    user.last_name?.toLowerCase().includes(trimmedSearch) ||
                    user.email?.toLowerCase().includes(trimmedSearch)
                );
                setAllUsers(filteredUsers);
                setTotalPages(Math.ceil(filteredUsers.length / itemsPerPage));
            }
            else{
                // Show all users by default
                let response = await userApi.all();
                setAllUsers(response.users);
                setTotalPages(Math.ceil(response.users.length / itemsPerPage));
                if(!search){
                    setActiveLetter('all');
                }
            }
        } catch (error) {
            console.error('UsersPage fetch error:', error);
            if (!handleAuthError(error)) {
                setAllUsers([]);
            }
        }
    }, [search, activeLetter, props.token, selectedNetwork, handleAuthError])

    useEffect(()=>{
        fetch()
    },[fetch])

    // Update displayed users when page or allUsers changes
    useEffect(() => {
        const startIndex = (pageItemActive - 1) * itemsPerPage;
        const endIndex = startIndex + itemsPerPage;
        const paginatedUsers = allUsers.slice(startIndex, endIndex);
        setUsers(paginatedUsers);
    }, [allUsers, pageItemActive, itemsPerPage])

    const fetchNetworks = useCallback(async () => {
        const networkApi = new NetworkApi(props.token);
        try {
            const response = await networkApi.all("Published", 1);
            setNetworks(response.networks || []);
        } catch (error) {
            console.error('Error fetching networks:', error);
            if (!handleAuthError(error)) {
                setNetworks([]);
            }
        }
    }, [props.token, handleAuthError]);

    useEffect(() => {
        fetchNetworks();
    }, [fetchNetworks]);

    const __handleSearch = (event) => {
        const newSearch = event.target.value;
        setSearch(newSearch);
        setActiveLetter('');
        // Don't clear selectedNetwork - allow search within network
        
        // Update URL parameters
        const newSearchParams = new URLSearchParams(searchParams);
        if (newSearch && newSearch.trim() !== '') {
            newSearchParams.set('search', newSearch);
        } else {
            newSearchParams.delete('search');
        }
        // Clear letter filter when searching
        newSearchParams.delete('letter');
        setSearchParams(newSearchParams);
    };

    const handleSearchByStatus =(event)=>{
        let value = event.target.value;
        setSearchStatus(value);
    }

    const handleNetworkChange = (event) => {
        const newNetwork = event.target.value;
        setSelectedNetwork(newNetwork);
        // Don't clear search - allow network filter with existing search
        setActiveLetter('');
        
        // Update URL parameters
        const newSearchParams = new URLSearchParams(searchParams);
        if (newNetwork && newNetwork !== '') {
            newSearchParams.set('network', newNetwork);
        } else {
            newSearchParams.delete('network');
        }
        // Clear letter filter when changing network
        newSearchParams.delete('letter');
        setSearchParams(newSearchParams);
    }
  
    const handleLetterClick = (letter) => {
        setSearch('')
        setSelectedNetwork('')
        setPageItemActive(1); 
        if(letter === 'all'){
            setActiveLetter('all'); 
        }else{
            setActiveLetter(letter); 
        }
        
        // Update URL parameters
        const newSearchParams = new URLSearchParams(searchParams);
        if (letter && letter !== 'all') {
            newSearchParams.set('letter', letter);
        } else {
            newSearchParams.delete('letter');
        }
        // Clear search and network when using letter filter
        newSearchParams.delete('search');
        newSearchParams.delete('network');
        setSearchParams(newSearchParams);
    };

    const handleSelectPage=(pageNumber)=>{
        setPageItemActive(pageNumber);
    }

    // Reset to page 1 when filters change
    useEffect(() => {
        setPageItemActive(1);
    }, [search, activeLetter, selectedNetwork])
    
    return (
        <div className='dashboard-content'>
            <DashboardHeader
                btnText="Add User" link='/user/new' />
            <div className='dashboard-content-container'>
                <div className='dashboard-content-header'>
                    <h2>Users</h2>
                    <div>
                        <div className='dashboard-content-search mx-2'>
                            <select                                
                                value={searchStatus}                                
                                className='dashboard-content-input search-status-input'
                                onChange={e => handleSearchByStatus(e)} >                                
                                <option value="All">All</option>    
                                <option value="Active">Active</option>    
                                <option value="Inactive">Inactive</option>
                                <option value="Pending">Pending</option>
                            </select>
                        </div>
                        <div className='dashboard-content-search'>
                            <input
                                type='text'
                                value={search}
                                placeholder='Search by name or email..'
                                className='dashboard-content-input'
                                onChange={e => __handleSearch(e)} />
                        </div>
                        <div className='dashboard-content-search search-city-input'>
                            <select
                                value={selectedNetwork}
                                className='dashboard-content-input'
                                onChange={e => handleNetworkChange(e)}>
                                <option value=''>Filter by Network</option>
                                {networks.map((network) => (
                                    <option key={network.id} value={network.id}>
                                        {network.name}
                                    </option>
                                ))}
                            </select>
                        </div>
                    </div>
                </div>
                <LetterFilter activeLetter={activeLetter} onLetterClick={handleLetterClick}/>
                <div className="users-custom-popup">
                    <PopupboxContainer />
                </div>
                    {
                        users.length > 0 ?
                            <UsersTable 
                                users={users} 
                                {...props} 
                                refresh={fetch} 
                                tenantId={selectedNetwork || tenantId}
                                isNetworkView={!!selectedNetwork}
                                search={search}
                                selectedNetwork={selectedNetwork}
                                activeLetter={activeLetter}
                            />
                            :
                            <div className='dashboard-content-footer'>
                                <span className='empty-table'>No data</span>
                            </div>
                    }
                <Pagination totalPages={totalPages} pageItemActive={pageItemActive} handleSelectItem={handleSelectPage}/>
            </div>
        </div>
    )
}

export default UsersPage
